import { useEffect, useState, useRef } from "react";
import { MapPin, Monitor, CheckCircle, AlertTriangle, DollarSign } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface MediaOwnerStatsProps {
  totalLocations: number;
  totalScreens: number;
  activeCampaigns: number;
  maintenanceCount: number;
  totalRevenue?: number;
}

function useCountUp(target: number, duration = 1200, startDelay = 0) {
  const [count, setCount] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    const start = prevTarget.current;
    const diff = target - start;

    const timer = setTimeout(() => {
      const startTime = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(start + diff * ease));
        if (progress < 1) requestAnimationFrame(tick);
        else prevTarget.current = target;
      };
      requestAnimationFrame(tick);
    }, startDelay);

    return () => clearTimeout(timer);
  }, [target, duration, startDelay]);

  return count;
}

interface StatCardProps {
  label: string;
  rawValue: number | string;
  countValue?: number;
  prefix?: string;
  suffix?: string;
  icon: React.ElementType;
  gradient: string;
  iconColor: string;
  delay: string;
}

const StatCard = ({ label, rawValue, countValue, prefix = '', suffix = '', icon: Icon, gradient, iconColor, delay }: StatCardProps) => {
  const displayValue = countValue !== undefined
    ? `${prefix}${countValue.toLocaleString()}${suffix}`
    : rawValue;

  return (
    <div
      className="relative rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fade-in"
      style={{ animationDelay: delay }}
    >
      {/* Top gradient accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${gradient}`} />

      {/* Watermark icon */}
      <div className="absolute -right-3 -top-3 opacity-[0.06]">
        <Icon className={`h-20 w-20 ${iconColor}`} />
      </div>

      <div className="p-5 relative z-10">
        {/* Icon chip */}
        <div className={`inline-flex p-2.5 rounded-xl ${gradient.replace('bg-gradient-to-r ', '')} mb-3`}
          style={{ background: 'transparent' }}>
          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient}`}>
            <Icon className={`h-5 w-5 text-white`} />
          </div>
        </div>

        {/* Value */}
        <p className="text-2xl font-bold text-foreground tabular-nums tracking-tight leading-none mb-1.5">
          {displayValue}
        </p>

        {/* Label */}
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>

        {/* Decorative bottom line */}
        <div className={`mt-4 h-px bg-gradient-to-r ${gradient} opacity-20`} />
      </div>
    </div>
  );
};

export const MediaOwnerStats = ({ totalLocations, totalScreens, activeCampaigns, maintenanceCount, totalRevenue = 0 }: MediaOwnerStatsProps) => {
  const { language } = useLanguage();
  const animLocations = useCountUp(totalLocations, 1200, 0);
  const animScreens = useCountUp(totalScreens, 1200, 80);
  const animCampaigns = useCountUp(activeCampaigns, 1200, 160);
  const animMaintenance = useCountUp(maintenanceCount, 1200, 240);
  const animRevenue = useCountUp(totalRevenue, 1200, 320);

  const stats: StatCardProps[] = [
    {
      label: language === 'ar' ? 'إجمالي المواقع' : 'Total Locations',
      rawValue: totalLocations,
      countValue: animLocations,
      icon: MapPin,
      gradient: 'from-blue-500 to-blue-600',
      iconColor: 'text-blue-500',
      delay: '0ms',
    },
    {
      label: language === 'ar' ? 'إجمالي الشاشات' : 'Total Screens',
      rawValue: totalScreens,
      countValue: animScreens,
      icon: Monitor,
      gradient: 'from-violet-500 to-violet-600',
      iconColor: 'text-violet-500',
      delay: '80ms',
    },
    {
      label: language === 'ar' ? 'الحملات النشطة' : 'Active Campaigns',
      rawValue: activeCampaigns,
      countValue: animCampaigns,
      icon: CheckCircle,
      gradient: 'from-emerald-500 to-emerald-600',
      iconColor: 'text-emerald-500',
      delay: '160ms',
    },
    {
      label: language === 'ar' ? 'قيد الصيانة' : 'In Maintenance',
      rawValue: maintenanceCount,
      countValue: animMaintenance,
      icon: AlertTriangle,
      gradient: 'from-amber-500 to-orange-500',
      iconColor: 'text-amber-500',
      delay: '240ms',
    },
    {
      label: language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue',
      rawValue: totalRevenue,
      countValue: animRevenue,
      suffix: ' SAR',
      icon: DollarSign,
      gradient: 'from-teal-500 to-cyan-500',
      iconColor: 'text-teal-500',
      delay: '320ms',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};
